import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, approveToken, formatTransactionError } from '../utils/transactionHelper';
import './GameDeposit.css';

const GameDeposit = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, tokenContract, CONTRACT_ADDRESS, TOKEN_ADDRESS } = web3Context;

  const [depositAmount, setDepositAmount] = useState('');
  const [gameBalance, setGameBalance] = useState('0');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [totalGameDeposits, setTotalGameDeposits] = useState('0');
  const [loading, setLoading] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);

  useEffect(() => {
    if (account && contract && tokenContract) {
      loadBalances();
      loadDepositHistory();
    }
  }, [account, contract, tokenContract]);

  const loadBalances = async () => {
    try {
      const gBal = await contract.methods.getGameDeposit(account).call();
      setGameBalance(web3.utils.fromWei(gBal.toString(), 'ether'));

      const tBal = await tokenContract.methods.balanceOf(account).call();
      setTokenBalance(web3.utils.fromWei(tBal.toString(), 'ether'));

      const analytics = await contract.methods.getAnalytics().call();
      setTotalGameDeposits(web3.utils.fromWei(analytics.totalGameDeposits.toString(), 'ether'));
    } catch (error) {
      console.error('Error loading game deposit balances:', error);
    }
  };

  const loadDepositHistory = async () => {
    try {
      const events = await contract.getPastEvents('GameDeposit', {
        filter: { user: account },
        fromBlock: 0,
        toBlock: 'latest'
      });

      const history = events.map(event => ({
        amount: web3.utils.fromWei(event.returnValues.amount.toString(), 'ether'),
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash
      }));

      setDepositHistory(history.reverse());
    } catch (error) {
      console.error('Error loading deposit history:', error);
    }
  };

  const handleDeposit = async () => {
    const depNum = parseFloat(depositAmount);
    if (isNaN(depNum) || depNum <= 0) {
      alert('Please enter a valid positive token amount');
      return;
    }

    if (depNum > parseFloat(tokenBalance)) {
      alert(`Insufficient token balance! You have ${tokenBalance} Tokens.`);
      return;
    }

    setLoading(true);
    try {
      const amountWei = web3.utils.toWei(depositAmount, 'ether');

      // Step 1: Approve Token transfer
      await approveToken(web3Context, TOKEN_ADDRESS, CONTRACT_ADDRESS, amountWei);

      // Step 2: Make game deposit
      const method = contract.methods.gameDeposit(amountWei);
      await sendContractTransaction(web3Context, method);

      alert(`Successfully deposited ${depositAmount} tokens into Gaming Escrow!`);
      setDepositAmount('');
      loadBalances();
      loadDepositHistory();
    } catch (error) {
      console.error('Error making deposit:', error);
      alert('Game deposit failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const setPercentage = (pct) => {
    const maxVal = parseFloat(tokenBalance);
    const calculated = (maxVal * (pct / 100)).toFixed(4);
    setDepositAmount(calculated);
  };

  return (
    <div className="game-wrapper">
      <div className="game-banner">
        <div className="banner-text">
          <h2>🎮 Gaming Escrow Deposit</h2>
          <p>Escrow target tokens into your gaming balance for decentralized gaming activities.</p>
        </div>
        <div className="balance-badge">
          <span className="lbl">Your Game Balance</span>
          <span className="val">{parseFloat(gameBalance).toLocaleString()} Tokens</span>
        </div>
      </div>

      <div className="game-grid">
        <div className="deposit-card">
          <h3>Make Escrow Deposit</h3>
          <div className="input-group">
            <div className="lbl-row">
              <label>Token Deposit Amount</label>
              <span className="avail">Wallet Token Bal: {parseFloat(tokenBalance).toLocaleString()}</span>
            </div>
            <div className="input-wrapper">
              <input
                type="number"
                placeholder="0.00"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                disabled={loading}
              />
              <button className="max-btn" onClick={() => setDepositAmount(tokenBalance)}>MAX</button>
            </div>
            <div className="pct-row">
              <button onClick={() => setPercentage(25)} disabled={loading}>25%</button>
              <button onClick={() => setPercentage(50)} disabled={loading}>50%</button>
              <button onClick={() => setPercentage(75)} disabled={loading}>75%</button>
              <button onClick={() => setPercentage(100)} disabled={loading}>100%</button>
            </div>
          </div>

          {depositAmount && parseFloat(depositAmount) > 0 && (
            <div className="preview-box">
              <div className="p-row">
                <span>Deposit Amount:</span>
                <span>{parseFloat(depositAmount).toLocaleString()} Tokens</span>
              </div>
              <div className="p-row">
                <span>New Game Balance:</span>
                <span className="highlight">
                  {(parseFloat(gameBalance) + parseFloat(depositAmount)).toLocaleString()} Tokens
                </span>
              </div>
            </div>
          )}

          <button
            className="btn-submit-deposit"
            onClick={handleDeposit}
            disabled={loading || !depositAmount || parseFloat(depositAmount) <= 0}
          >
            {loading ? 'Processing Approval & Deposit...' : '🎮 Approve Tokens & Deposit to Game Escrow'}
          </button>
        </div>

        <div className="info-side-card">
          <h3>📊 Platform Escrow Stats</h3>
          <div className="side-stat">
            <span className="lbl">Total Protocol Game Escrows</span>
            <span className="val">{parseFloat(totalGameDeposits).toLocaleString()} Tokens</span>
          </div>
          <div className="notes-box">
            <h4>Important Rules:</h4>
            <ul>
              <li>Deposited tokens are credited to `gameDepositBalance[user]`.</li>
              <li>Tokens are securely held in the P2PTrade smart contract.</li>
              <li>Protected by anti-bot cooldown security timers.</li>
            </ul>
          </div>

          {depositHistory.length > 0 && (
            <div className="history-mini-box">
              <h4>Your Escrow History ({depositHistory.length})</h4>
              <div className="mini-history-list">
                {depositHistory.map((item, idx) => (
                  <div key={idx} className="mini-h-item">
                    <span>Block #{item.blockNumber}</span>
                    <span className="amount">+{parseFloat(item.amount).toLocaleString()} Tokens</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameDeposit;